import styled, { keyframes, css } from 'styled-components'

// ─── Animations ─────────────────────────────────────────────────────────────
export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`

export const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.03); }
`

// ─── Page wrapper ────────────────────────────────────────────────────────────
export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px 100px 20px;
  gap: 20px;
  font-family: 'Montserrat', sans-serif;
  min-height: 100vh;
  color: ${(props) => props.theme.textPrimary};
`

// ─── ETAPA 1 — Filtros / Wizard ──────────────────────────────────────────────
export const FilterCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, #00205b 0%, #002b80 60%, #001842 100%)'};
  border: ${(props) =>
    props.theme.name === 'light' ? '1px solid #cbd5e1' : 'none'};
  border-radius: 24px;
  padding: 40px;
  max-width: 780px;
  width: 100%;
  margin: 40px auto 0;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 8px 30px rgba(0, 0, 0, 0.08)'
      : '0 8px 40px rgba(0, 0, 0, 0.45)'};
  animation: ${fadeInUp} 0.4s ease;

  @media (max-width: 768px) {
    padding: 24px 16px;
    margin: 16px auto 0;
  }
`

export const FilterTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${(props) => (props.theme.name === 'light' ? '#001f5c' : '#a6c10b')};
  margin-bottom: 6px;
  letter-spacing: 0.5px;
`

export const FilterSubtitle = styled.p`
  font-size: 0.85rem;
  color: ${(props) =>
    props.theme.name === 'light' ? '#475569' : 'rgba(255, 255, 255, 0.6)'};
  margin-bottom: 32px;
`

export const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;
`

export const FilterLabel = styled.label`
  font-size: 0.78rem;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'light' ? '#001f5c' : '#a6c10b')};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`

export const FilterDivider = styled.hr`
  border: none;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'light'
        ? '#e2e8f0'
        : 'rgba(255, 255, 255, 0.1)'};
  margin: 8px 0 24px;
`

// Lista de distribuidores com scroll
export const DistList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'};
  border-radius: 10px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.2)'};
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  scrollbar-color: ${(props) =>
    props.theme.name === 'light' ? '#0284c7' : '#a6c10b'} transparent;
  scrollbar-width: thin;

  ::-webkit-scrollbar {
    display: block;
    width: 4px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.name === 'light' ? '#94a3b8' : '#a6c10b'};
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      props.theme.name === 'light' ? '#64748b' : '#c2dc1b'};
  }
`

export const DistItem = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) =>
    p.$selected
      ? p.theme.name === 'light'
        ? '#e0f2fe'
        : 'rgba(166, 193, 11, 0.18)'
      : 'transparent'};
  transition: background 0.18s;
  font-size: 0.85rem;
  font-weight: ${(p) => (p.$selected ? 600 : 400)};
  color: ${(p) =>
    p.$selected
      ? p.theme.name === 'light'
        ? '#0369a1'
        : '#a6c10b'
      : p.theme.name === 'light'
      ? '#1e293b'
      : 'rgba(255,255,255,0.8)'};

  &:hover {
    background: ${(p) =>
      p.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.07)'};
  }

  input { display: none; }
`

export const CheckboxBox = styled.span<{ $selected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid
    ${(props) =>
      props.$selected
        ? props.theme.name === 'light'
          ? '#0284c7'
          : '#a6c10b'
        : props.theme.name === 'light'
        ? '#94a3b8'
        : 'rgba(255, 255, 255, 0.4)'};
  background: ${(props) =>
    props.$selected
      ? props.theme.name === 'light'
        ? '#0284c7'
        : '#a6c10b'
      : props.theme.name === 'light'
      ? '#ffffff'
      : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;

  svg {
    color: ${(props) =>
      props.theme.name === 'light' ? '#ffffff' : '#001842'};
  }
`

// Seletor de Tipo de Período (Mensal / Semanal vs Year to Date YTD)
export const PeriodTypeContainer = styled.div`
  display: flex;
  background: ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 0, 0, 0.35)')};
  padding: 4px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.12)')};
  gap: 6px;
  margin-bottom: 14px;
`

export const PeriodTypeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 9px;
  border: none;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  transition: all 0.2s cubic-bezier(0.25, 1, 0.5, 1);

  background: ${(p) =>
    p.$active
      ? p.theme.name === 'light'
        ? '#ffffff'
        : 'linear-gradient(135deg, #a6c10b 0%, #8ea709 100%)'
      : 'transparent'};
  color: ${(p) =>
    p.$active
      ? p.theme.name === 'light'
        ? '#0284c7'
        : '#001842'
      : p.theme.name === 'light'
      ? '#64748b'
      : 'rgba(255, 255, 255, 0.65)'};
  box-shadow: ${(p) =>
    p.$active
      ? p.theme.name === 'light'
        ? '0 2px 8px rgba(0, 0, 0, 0.08)'
        : '0 2px 12px rgba(166, 193, 11, 0.4)'
      : 'none'};

  &:hover {
    color: ${(p) =>
      p.$active
        ? p.theme.name === 'light'
          ? '#0284c7'
          : '#001842'
        : p.theme.name === 'light'
        ? '#0f172a'
        : '#ffffff'};
  }
`

export const YtdPresetsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`

export const YtdPresetButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;

  background: ${(p) =>
    p.$active
      ? p.theme.name === 'light'
        ? 'rgba(2, 132, 199, 0.15)'
        : 'rgba(166, 193, 11, 0.2)'
      : p.theme.name === 'light'
      ? '#f8fafc'
      : 'rgba(255, 255, 255, 0.06)'};
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme.name === 'light'
          ? '#0284c7'
          : '#a6c10b'
        : p.theme.name === 'light'
        ? '#cbd5e1'
        : 'rgba(255, 255, 255, 0.15)'};
  color: ${(p) =>
    p.$active
      ? p.theme.name === 'light'
        ? '#0369a1'
        : '#a6c10b'
      : p.theme.name === 'light'
      ? '#475569'
      : 'rgba(255, 255, 255, 0.8)'};

  &:hover {
    background: ${(p) =>
      p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.1)' : 'rgba(166, 193, 11, 0.15)'};
    border-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  }
`

export const MonthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

export const MonthChip = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 6px;
  border-radius: 9px;
  cursor: pointer;
  user-select: none;
  font-family: 'Montserrat', sans-serif;
  transition: all 0.15s ease;

  background: ${(p) =>
    p.$selected
      ? p.theme.name === 'light'
        ? '#e0f2fe'
        : 'rgba(166, 193, 11, 0.2)'
      : p.theme.name === 'light'
      ? '#f8fafc'
      : 'rgba(0, 0, 0, 0.25)'};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? p.theme.name === 'light'
          ? '#0284c7'
          : '#a6c10b'
        : p.theme.name === 'light'
        ? '#cbd5e1'
        : 'rgba(255, 255, 255, 0.12)'};
  color: ${(p) =>
    p.$selected
      ? p.theme.name === 'light'
        ? '#0369a1'
        : '#a6c10b'
      : p.theme.name === 'light'
      ? '#334155'
      : 'rgba(255, 255, 255, 0.85)'};
  font-weight: ${(p) => (p.$selected ? 700 : 500)};
  font-size: 0.82rem;

  &:hover {
    background: ${(p) =>
      p.$selected
        ? p.theme.name === 'light'
          ? '#bae6fd'
          : 'rgba(166, 193, 11, 0.28)'
        : p.theme.name === 'light'
        ? '#f1f5f9'
        : 'rgba(255, 255, 255, 0.08)'};
  }

  span.idx {
    font-size: 0.65rem;
    opacity: 0.6;
    font-weight: 400;
  }
`

export const PotentialGapBadge = styled.div<{ $hasGap?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 600;
  background: ${(p) =>
    p.$hasGap
      ? p.theme.name === 'light'
        ? 'rgba(217, 119, 6, 0.1)'
        : 'rgba(245, 158, 11, 0.12)'
      : p.theme.name === 'light'
      ? 'rgba(2, 132, 199, 0.1)'
      : 'rgba(166, 193, 11, 0.12)'};
  border: 1px solid
    ${(p) =>
      p.$hasGap
        ? p.theme.name === 'light'
          ? 'rgba(217, 119, 6, 0.35)'
          : 'rgba(245, 158, 11, 0.35)'
        : p.theme.name === 'light'
        ? 'rgba(2, 132, 199, 0.3)'
        : 'rgba(166, 193, 11, 0.25)'};
  color: ${(p) =>
    p.$hasGap
      ? p.theme.name === 'light'
        ? '#b45309'
        : '#fbbf24'
      : p.theme.name === 'light'
      ? '#0369a1'
      : '#a6c10b'};
  width: fit-content;

  span.gap {
    color: ${(p) => (p.theme.name === 'light' ? '#d97706' : '#f59e0b')};
    font-weight: 700;
  }

  span.pct {
    opacity: 0.85;
    font-weight: 500;
  }
`

// Seleção de semana (checkboxes)
export const SemanasGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

export const SemanaCheckbox = styled.label<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) =>
    p.$selected
      ? p.theme.name === 'light'
        ? '#e0f2fe'
        : 'rgba(166, 193, 11, 0.18)'
      : p.theme.name === 'light'
      ? '#f8fafc'
      : 'rgba(0, 0, 0, 0.25)'};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? p.theme.name === 'light'
          ? '#0284c7'
          : '#a6c10b'
        : p.theme.name === 'light'
        ? '#cbd5e1'
        : 'rgba(255, 255, 255, 0.15)'};
  transition: all 0.2s;
  font-size: 0.85rem;
  font-weight: ${(p) => (p.$selected ? 700 : 500)};
  color: ${(p) =>
    p.$selected
      ? p.theme.name === 'light'
        ? '#0369a1'
        : '#a6c10b'
      : p.theme.name === 'light'
      ? '#334155'
      : 'rgba(255, 255, 255, 0.85)'};
  user-select: none;

  &:hover {
    background: ${(p) =>
      p.$selected
        ? p.theme.name === 'light'
          ? '#bae6fd'
          : 'rgba(166, 193, 11, 0.25)'
        : p.theme.name === 'light'
        ? '#f1f5f9'
        : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${(p) =>
      p.$selected
        ? p.theme.name === 'light'
          ? '#0284c7'
          : '#a6c10b'
        : p.theme.name === 'light'
        ? '#94a3b8'
        : 'rgba(255, 255, 255, 0.35)'};
  }

  input { display: none; }
`

// Botões de semana (chips)
export const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

export const Chip = styled.button<{ $active: boolean }>`
  padding: 8px 18px;
  border-radius: 20px;
  border: 1.5px solid
    ${(p) =>
      p.$active
        ? p.theme.name === 'light'
          ? '#0284c7'
          : '#a6c10b'
        : p.theme.name === 'light'
        ? '#cbd5e1'
        : 'rgba(255,255,255,0.2)'};
  background: ${(p) =>
    p.$active
      ? p.theme.name === 'light'
        ? '#0284c7'
        : '#a6c10b'
      : p.theme.name === 'light'
      ? '#ffffff'
      : 'transparent'};
  color: ${(p) =>
    p.$active
      ? p.theme.name === 'light'
        ? '#ffffff'
        : '#001842'
      : p.theme.name === 'light'
      ? '#334155'
      : 'rgba(255, 255, 255, 0.75)'};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Montserrat', sans-serif;

  &:hover {
    border-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    color: ${(p) =>
      p.$active
        ? p.theme.name === 'light'
          ? '#ffffff'
          : '#001842'
        : p.theme.name === 'light'
        ? '#0284c7'
        : '#a6c10b'};
  }
`

// Seletores de mês e ano
export const SelectRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

export const StyledSelect = styled.select`
  flex: 1;
  min-width: 120px;
  background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.3)')} !important;
  color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')} !important;
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 10px;
  padding: 10px 14px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.88rem;
  cursor: pointer;
  appearance: none;

  option {
    background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : '#002b80')} !important;
    color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')} !important;
  }

  &:focus { outline: none; border-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')}; }
`

// Search das cidades
export const SearchInput = styled.input`
  width: 100%;
  background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.2)')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 10px;
  padding: 10px 14px;
  color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  font-family: 'Montserrat', sans-serif;
  font-size: 0.88rem;
  margin-bottom: 8px;

  &::placeholder { color: ${(p) => (p.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)')}; }
  &:focus { outline: none; border-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')}; }
`

// Botão principal (Gerar Resumo)

export const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

export const ButtonSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(0, 24, 66, 0.25);
  border-top-color: #001842;
  border-radius: 50%;
  animation: ${spinAnimation} 0.75s linear infinite;
  display: inline-block;
  box-sizing: border-box;
`

export const GenerateButton = styled.button<{ $loading?: boolean }>`
  width: 100%;
  height: 48px;
  min-height: 48px;
  max-height: 48px;
  padding: 0 24px;
  margin-top: 8px;
  background: linear-gradient(135deg, #a6c10b, #c8e412);
  color: #001842;
  border: none;
  border-radius: 12px;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: ${(p) => (p.$loading ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$loading ? 0.85 : 1)};
  transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(166, 193, 11, 0.4);
  }
`

export const SelectAllBtn = styled.button`
  background: transparent;
  border: none;
  color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
  font-family: 'Montserrat', sans-serif;

  &:hover { color: ${(p) => (p.theme.name === 'light' ? '#0369a1' : '#c8e412')}; }
`

// ─── ETAPA 2 — Resultados ────────────────────────────────────────────────────

export const backButtonPulse = keyframes`
  0% {
    transform: scale(1) translateX(0);
    box-shadow: 0 4px 18px rgba(166, 193, 11, 0.45);
  }
  35% {
    transform: scale(0.91) translateX(-8px);
    box-shadow: 0 0 32px rgba(166, 193, 11, 0.95), 0 0 0 8px rgba(166, 193, 11, 0.35);
    filter: brightness(1.22);
  }
  70% {
    transform: scale(0.96) translateX(-4px);
    box-shadow: 0 0 22px rgba(166, 193, 11, 0.75);
  }
  100% {
    transform: scale(1) translateX(0);
  }
`

// Barra superior de ações principal com grid 3 colunas (mantendo centro exato)
export const TopHeaderBar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 16px 0 6px;
  animation: ${fadeInUp} 0.3s ease;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
  }
`

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`

export const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  @media (max-width: 900px) {
    justify-content: flex-end;
  }
`

// Sub-barra com tags de filtros e busca
export const SubHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  padding: 4px 0 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

// Barra legado mantida para compatibilidade
export const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 0 4px;
  animation: ${fadeInUp} 0.3s ease;

  @media (max-width: 768px) { flex-direction: column; align-items: flex-start; }
`

export const ResultTitle = styled.h1`
  font-size: 1.85rem;
  font-weight: 900;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  letter-spacing: 0.5px;
  text-align: center;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-shadow: ${(props) =>
    props.theme.name === 'light' ? 'none' : '0 3px 14px rgba(0, 0, 0, 0.6)'};

  span {
    color: ${(props) => (props.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    text-shadow: ${(props) =>
      props.theme.name === 'light'
        ? 'none'
        : '0 0 25px rgba(166, 193, 11, 0.6), 0 2px 10px rgba(0, 0, 0, 0.5)'};
    position: relative;
    display: inline-block;
  }

  @media (max-width: 1024px) {
    font-size: 1.55rem;
  }

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`

export const FilterTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

export const FilterTag = styled.span`
  background: ${(props) => (props.theme.name === 'light' ? 'rgba(2, 132, 199, 0.12)' : 'rgba(166, 193, 11, 0.15)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? 'rgba(2, 132, 199, 0.35)' : 'rgba(166, 193, 11, 0.35)')};
  color: ${(props) => (props.theme.name === 'light' ? '#0369a1' : '#a6c10b')};
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 600;
`

export const ActionButton = styled.button<{ $variant?: 'outline' | 'ghost' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 115px;
  padding: 10px 22px;
  border-radius: 10px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s;

  ${(p) =>
    p.$variant === 'outline'
      ? `background: ${p.theme.name === 'light' ? '#ffffff' : 'transparent'};
         border: 1.5px solid ${p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255,255,255,0.25)'};
         color: ${p.theme.name === 'light' ? '#1e293b' : '#ffffff'};
         &:hover { border-color: #0284c7; color: #0284c7; }`
      : p.$variant === 'ghost'
      ? `background: transparent; border: none;
         color: ${p.theme.name === 'light' ? '#64748b' : 'rgba(255,255,255,0.6)'};
         &:hover { color: ${p.theme.name === 'light' ? '#0f172a' : '#ffffff'}; }`
      : `background: ${p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.12)' : 'rgba(166,193,11,0.15)'};
         border: 1px solid ${p.theme.name === 'light' ? '#0284c7' : '#a6c10b'};
         color: ${p.theme.name === 'light' ? '#0284c7' : '#a6c10b'};
         &:hover { background: ${p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.22)' : 'rgba(166,193,11,0.28)'}; }`}
`

// Botão de destaque "Voltar" com cor vibrante, cumprimento ampliado e animação tátil no clique
export const BackButton = styled.button<{ $isClicking?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 155px;
  padding: 12px 32px;
  border-radius: 12px;
  background: linear-gradient(135deg, #a6c10b 0%, #8ea709 100%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #001842;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.4px;
  cursor: pointer;
  box-shadow: 0 4px 18px rgba(166, 193, 11, 0.45);
  transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  flex-shrink: 0;
  user-select: none;

  svg {
    transition: transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 24px rgba(166, 193, 11, 0.65);
    background: linear-gradient(135deg, #b8d413 0%, #a6c10b 100%);

    svg {
      transform: translateX(-5px);
    }
  }

  &:active {
    transform: scale(0.92) translateX(-6px);
    box-shadow: 0 0 26px rgba(166, 193, 11, 0.85);
  }

  ${(p) =>
    p.$isClicking &&
    css`
      animation: ${backButtonPulse} 0.35s cubic-bezier(0.25, 1, 0.5, 1) forwards;
      svg {
        transform: translateX(-10px) scale(1.2) !important;
      }
    `}
`

export const SearchBar = styled.div`
  position: relative;
  min-width: 220px;

  input {
    width: 100%;
    background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)')};
    border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
    border-radius: 10px;
    padding: 8px 14px 8px 36px;
    color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    font-family: 'Montserrat', sans-serif;
    font-size: 0.85rem;

    &::placeholder { color: ${(p) => (p.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)')}; }
    &:focus { outline: none; border-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')}; }
  }

  svg {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: ${(p) => (p.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)')};
    font-size: 15px;
    pointer-events: none;
  }
`

// Cards de resumo superiores (R$, CX e Cobertura)
export const SummaryCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  animation: ${fadeInUp} 0.4s ease 0.05s both;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

export const SummaryCard = styled.div`
  background: ${(p) =>
    p.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, #00205b 0%, #002b80 100%)'};
  border: 1px solid
    ${(p) =>
      p.theme.name === 'light' ? '#e2e8f0' : 'rgba(166, 193, 11, 0.2)'};
  border-radius: 18px;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: ${(p) =>
    p.theme.name === 'light'
      ? '0 4px 16px rgba(0, 0, 0, 0.06)'
      : '0 4px 24px rgba(0, 0, 0, 0.3)'};
`

export const SummaryLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`

export const SummaryValue = styled.span`
  font-size: 1.9rem;
  font-weight: 800;
  color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  line-height: 1;
`

// Grid principal de 3 colunas
export const MainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  animation: ${fadeInUp} 0.4s ease 0.1s both;

  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`

export const GridColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  background: ${(p) =>
    p.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(180deg, #00205b 0%, #002b80 100%)'};
  border: ${(p) =>
    p.theme.name === 'light' ? '1px solid #e2e8f0' : 'none'};
  border-radius: 18px;
  overflow: hidden;
  box-shadow: ${(p) =>
    p.theme.name === 'light'
      ? '0 4px 16px rgba(0, 0, 0, 0.06)'
      : '0 4px 24px rgba(0, 0, 0, 0.3)'};
`

export const ColumnHeader = styled.div`
  padding: 16px 20px;
  background: ${(p) =>
    p.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.2)'};
  border-bottom: 1px solid
    ${(p) =>
      p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)'};
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-titles {
    display: flex;
    flex-direction: column;
  }

  h2 {
    font-size: 0.88rem;
    font-weight: 800;
    color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#a6c10b')};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 2px;
  }

  p {
    font-size: 0.72rem;
    color: ${(p) =>
      p.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.5)'};
    margin: 0;
  }
`

export const ColumnCompareButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.06)')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.16)')};
  color: ${(p) => (p.theme.name === 'light' ? '#475569' : 'rgba(255, 255, 255, 0.75)')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(p) => (p.theme.name === 'light' ? '#e0f2fe' : 'rgba(166, 193, 11, 0.2)')};
    border-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    transform: scale(1.08);
    box-shadow: 0 0 10px ${(p) => (p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.25)' : 'rgba(166, 193, 11, 0.35)')};
  }

  &:active {
    transform: scale(0.95);
  }
`

export const ColumnBody = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 560px;
  overflow-y: auto;
  padding: 8px 0;

  scrollbar-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')} transparent;
  scrollbar-width: thin;

  ::-webkit-scrollbar {
    display: block;
    width: 4px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(p) => (p.theme.name === 'light' ? '#94a3b8' : '#a6c10b')};
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${(p) => (p.theme.name === 'light' ? '#64748b' : '#c2dc1b')};
  }
`

export const FamiliaRow = styled.div<{ $index: number }>`
  display: flex;
  flex-direction: column;
  padding: 10px 20px;
  border-bottom: 1px solid ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)')};
  animation: ${fadeInUp} 0.3s ease ${(p) => p.$index * 0.03}s both;
  transition: background 0.15s;

  &:hover { background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.04)')}; }
  &:last-child { border-bottom: none; }
`

export const FamiliaName = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  margin-bottom: 4px;
`

export const FamiliaMetrics = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

export const MetricItem = styled.span<{ $accent?: boolean }>`
  font-size: 0.78rem;
  color: ${(p) => (p.$accent ? (p.theme.name === 'light' ? '#0284c7' : '#a6c10b') : p.theme.name === 'light' ? '#64748b' : 'rgba(255,255,255,0.65)')};
  font-weight: ${(p) => (p.$accent ? 600 : 400)};
`

export const ShareBar = styled.div<{ $percent: number }>`
  height: 3px;
  background: ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 2px;
  margin-top: 6px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${(p) => p.$percent}%;
    background: linear-gradient(90deg, #a6c10b, #c8e412);
    border-radius: 2px;
    transition: width 0.8s ease;
  }
`

// ── Componentes de Expansão de SKUs ──────────────────────────────────────────

export const FamiliaHeader = styled.div<{ $clickable?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  user-select: none;
`

export const FamiliaTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

export const SkuBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  background: ${(p) => (p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.12)' : 'rgba(166, 193, 11, 0.15)')};
  color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.25)' : 'rgba(166, 193, 11, 0.3)')};
  white-space: nowrap;
`

export const ExpandChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.5)')};
  transition: transform 0.2s ease, color 0.2s ease;
  transform: ${(p) => (p.$expanded ? 'rotate(180deg)' : 'rotate(0deg)')};

  ${FamiliaHeader}:hover & {
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  }
`

export const SkuListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding: 8px 10px;
  background: ${(p) => (p.theme.name === 'light' ? 'rgba(241, 245, 249, 0.7)' : 'rgba(0, 16, 46, 0.55)')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 8px;
`

export const SkuRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 5px 8px;
  border-radius: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.theme.name === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.04)')};
  }
`

export const SkuInfoGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

export const SkuCode = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 4px;
  background: ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
  color: ${(p) => (p.theme.name === 'light' ? '#475569' : '#cbd5e1')};
  white-space: nowrap;
`

export const SkuDesc = styled.span`
  font-size: 0.73rem;
  font-weight: 500;
  color: ${(p) => (p.theme.name === 'light' ? '#1e293b' : 'rgba(255, 255, 255, 0.85)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const SkuMetrics = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  font-size: 0.72rem;
`

export const SkuMetricValue = styled.span<{ $accent?: boolean }>`
  color: ${(p) => (p.$accent ? (p.theme.name === 'light' ? '#0284c7' : '#a6c10b') : p.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.65)')};
  font-weight: ${(p) => (p.$accent ? 600 : 400)};
`

export const ToggleAllSkusButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-family: 'Montserrat', sans-serif;

  &:hover {
    background: ${(p) => (p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.1)' : 'rgba(166, 193, 11, 0.12)')};
  }
`

// Estado vazio
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
  color: ${(p) => (p.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)')};
  font-size: 0.85rem;
  text-align: center;
`

// Total no topo de cada coluna
export const ColumnTotalHeader = styled.div`
  padding: 12px 18px;
  background: ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 16, 46, 0.7)')};
  border-bottom: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.12)')};
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const ColumnTotalTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
`

export const ColumnTotalMetrics = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`

// Aliases
export const ColumnFooter = ColumnTotalHeader
export const ColumnFooterTitle = ColumnTotalTitle
export const ColumnFooterMetrics = ColumnTotalMetrics

export const TotalMetricItem = styled.span<{ $accent?: boolean }>`
  font-size: 0.86rem;
  font-weight: 700;
  color: ${(p) => (p.$accent ? (p.theme.name === 'light' ? '#0284c7' : '#a6c10b') : p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
`

// Skeleton loader
export const SkeletonRow = styled.div`
  height: 56px;
  margin: 6px 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite;
`

// ─── Componentes de Comparativo e Alerta vs Média ─────────────────────────────

export const AlertBadge = styled.span<{ $severity: 'yellow' | 'red' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.3px;
  cursor: help;
  transition: transform 0.15s;

  ${(p) =>
    p.$severity === 'red'
      ? `
    background: rgba(239, 68, 68, 0.22);
    border: 1px solid #ef4444;
    color: #ff6b6b;
    box-shadow: 0 0 8px rgba(239, 68, 68, 0.4);
  `
      : `
    background: rgba(245, 158, 11, 0.2);
    border: 1px solid #f59e0b;
    color: #fbbf24;
    box-shadow: 0 0 8px rgba(245, 158, 11, 0.35);
  `}

  &:hover {
    transform: scale(1.06);
  }
`

export const ComparisonTag = styled.span<{ $direction?: 'up' | 'down' | 'neutral'; $severity?: 'yellow' | 'red' | null }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;

  color: ${(p) =>
    p.$severity === 'red'
      ? '#ff6b6b'
      : p.$severity === 'yellow'
      ? '#fbbf24'
      : p.$direction === 'up'
      ? '#a6c10b'
      : p.$direction === 'down'
      ? 'rgba(255, 255, 255, 0.75)'
      : 'rgba(255, 255, 255, 0.5)'};
`

export const MetricItemWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
`

export const MetricTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const SummaryCardAlert = styled.div<{ $severity: 'yellow' | 'red' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.74rem;
  font-weight: 700;
  width: fit-content;

  ${(p) =>
    p.$severity === 'red'
      ? `
    background: rgba(239, 68, 68, 0.22);
    border: 1px solid #ef4444;
    color: #ff6b6b;
    box-shadow: 0 0 10px rgba(239, 68, 68, 0.4);
  `
      : `
    background: rgba(245, 158, 11, 0.2);
    border: 1px solid #f59e0b;
    color: #fbbf24;
    box-shadow: 0 0 10px rgba(245, 158, 11, 0.35);
  `}
`

// ─── Componentes de Comparativo e Alerta vs Meta (Threshold 1.5%) ──────────────

export const MetaComparisonTag = styled.span<{ $severity: 'green' | 'red' | 'subtle-yellow' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.69rem;
  font-weight: 700;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 6px;
  letter-spacing: 0.2px;

  ${(p) => {
    if (p.$severity === 'green') {
      return `
        background: rgba(46, 204, 113, 0.16);
        border: 1px solid rgba(46, 204, 113, 0.38);
        color: #2ecc71;
      `
    }
    if (p.$severity === 'red') {
      return `
        background: rgba(239, 68, 68, 0.16);
        border: 1px solid rgba(239, 68, 68, 0.38);
        color: #ff6b6b;
      `
    }
    // subtle-yellow (amarelo acinzentado discreto com pouco destaque)
    return `
      background: rgba(212, 196, 140, 0.08);
      border: 1px solid rgba(212, 196, 140, 0.22);
      color: rgba(215, 205, 160, 0.78);
    `
  }}
`

export const SummaryCardMetaInfo = styled.div<{ $severity: 'green' | 'red' | 'subtle-yellow' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  width: fit-content;

  ${(p) => {
    if (p.$severity === 'green') {
      return `
        background: rgba(46, 204, 113, 0.15);
        border: 1px solid rgba(46, 204, 113, 0.35);
        color: #2ecc71;
        box-shadow: 0 0 10px rgba(46, 204, 113, 0.2);
      `
    }
    if (p.$severity === 'red') {
      return `
        background: rgba(239, 68, 68, 0.15);
        border: 1px solid rgba(239, 68, 68, 0.35);
        color: #ff6b6b;
        box-shadow: 0 0 10px rgba(239, 68, 68, 0.2);
      `
    }
    return `
      background: rgba(212, 196, 140, 0.08);
      border: 1px solid rgba(212, 196, 140, 0.22);
      color: rgba(215, 205, 160, 0.85);
    `
  }}
`

// ─── Modal de Comparação Atual × Média de 3 Meses ─────────────────────────────

export const CompareModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 8, 24, 0.85);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeInUp} 0.25s ease both;
`

export const CompareModalContainer = styled.div`
  background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : '#02122c')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(166, 193, 11, 0.3)')};
  border-radius: 18px;
  box-shadow: ${(p) =>
    p.theme.name === 'light'
      ? '0 24px 60px rgba(0, 0, 0, 0.15)'
      : '0 24px 60px rgba(0, 0, 0, 0.8), 0 0 30px rgba(166, 193, 11, 0.15)'};
  width: 100%;
  max-width: 960px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Montserrat', sans-serif;
  animation: ${fadeInUp} 0.3s ease both;
`

export const CompareModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.35)')};
  border-bottom: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};

  .title-group {
    display: flex;
    flex-direction: column;
    gap: 4px;

    h3 {
      font-size: 1.05rem;
      font-weight: 800;
      color: ${(p) => (p.theme.name === 'light' ? '#001f5c' : '#a6c10b')};
      margin: 0;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    p {
      font-size: 0.74rem;
      color: ${(p) => (p.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.55)')};
      margin: 0;
    }
  }
`

export const CompareModalCloseBtn = styled.button`
  background: ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  color: ${(p) => (p.theme.name === 'light' ? '#475569' : '#ffffff')};
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 77, 77, 0.25);
    border-color: #ff4d4d;
    color: #ff4d4d;
    transform: scale(1.06);
  }
`

export const CompareModalSummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  padding: 16px 24px;
  background: ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 24, 60, 0.35)')};
  border-bottom: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.06)')};
`

export const CompareSummaryCard = styled.div`
  background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .card-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    letter-spacing: 0.5px;
  }

  .card-values {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;

    .current-val {
      font-size: 1.15rem;
      font-weight: 800;
      color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }

    .media-val {
      font-size: 0.8rem;
      color: ${(p) => (p.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.5)')};
    }
  }
`

export const CompareModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px;
  max-height: calc(90vh - 190px);

  scrollbar-color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')} transparent;
  scrollbar-width: thin;

  ::-webkit-scrollbar {
    display: block;
    width: 5px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${(p) => (p.theme.name === 'light' ? '#94a3b8' : '#a6c10b')};
    border-radius: 10px;
  }
`

export const CompareTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  thead th {
    background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.3)')};
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.5px;
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)')};
    position: sticky;
    top: 0;
    z-index: 2;

    &.right-align {
      text-align: right;
    }
  }

  tbody tr {
    border-bottom: 1px solid ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)')};
    transition: background 0.15s;

    &:hover {
      background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.04)')};
    }
  }

  tbody td {
    padding: 11px 14px;
    color: ${(p) => (p.theme.name === 'light' ? '#334155' : 'rgba(255, 255, 255, 0.85)')};

    &.familia-cell {
      font-weight: 700;
      color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }

    &.right-align {
      text-align: right;
    }
  }
`

// ─── Split Button de Geração (Gerar Resumo / Gerar Revisão em Grupo) ────────

export const SplitButtonWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  margin-top: 8px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(166, 193, 11, 0.25);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(166, 193, 11, 0.35);
  }
`

export const SplitMainButton = styled.button<{ $loading?: boolean }>`
  flex: 1;
  padding: 14px 18px;
  background: linear-gradient(135deg, #a6c10b, #c8e412);
  color: #001842;
  border: none;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: ${(p) => (p.$loading ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$loading ? 0.7 : 1)};
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const SplitDropdownToggle = styled.button<{ $open?: boolean }>`
  padding: 14px 16px;
  background: #94ad0a;
  color: #001842;
  border: none;
  border-left: 1px solid rgba(0, 24, 66, 0.2);
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #849c09;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const SplitDropdownMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : '#001842')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(166, 193, 11, 0.3)')};
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
  z-index: 100;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export const SplitDropdownItem = styled.button<{ $isGroup?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  cursor: pointer;
  text-align: left;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  transition: background 0.15s, color 0.15s;

  .item-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .badge-zip {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    background: ${(p) => (p.theme.name === 'light' ? '#e0f2fe' : 'rgba(56, 189, 248, 0.2)')};
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#38bdf8')};
  }

  &:hover {
    background: ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : 'rgba(166, 193, 11, 0.15)')};
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
  }
`

// ─── Modal de Progresso da Revisão em Grupo ─────────────────────────────────

export const GroupProgressModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`

export const GroupProgressModalCard = styled.div`
  width: 100%;
  max-width: 480px;
  background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : '#001842')};
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(166, 193, 11, 0.35)')};
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  text-align: center;
  font-family: 'Montserrat', sans-serif;

  .icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    margin: 0 auto;
    background: ${(p) => (p.theme.name === 'light' ? 'rgba(2, 132, 199, 0.12)' : 'rgba(166, 193, 11, 0.15)')};
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: ${(p) => (p.theme.name === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.7)')};
    line-height: 1.5;
  }

  .dist-current {
    font-weight: 700;
    color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    display: block;
    margin-top: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .btn-cancel {
    padding: 10px 20px;
    border-radius: 8px;
    border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.2)')};
    background: transparent;
    color: ${(p) => (p.theme.name === 'light' ? '#64748b' : '#94a3b8')};
    font-family: 'Montserrat', sans-serif;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    align-self: center;
    transition: all 0.2s;

    &:hover {
      background: rgba(239, 68, 68, 0.12);
      border-color: #ef4444;
      color: #ef4444;
    }
  }
`

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 5px;
  background: ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
  overflow: hidden;
  position: relative;
`

export const ProgressBarFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${(p) => Math.min(Math.max(p.$percent, 0), 100)}%;
  border-radius: 5px;
  background: linear-gradient(90deg, #a6c10b, #38bdf8);
  transition: width 0.3s ease;
`





// ==============================================================================
// 📅 Acompanhamento Diário — Styled Components
// ==============================================================================

export const SubmenuNavWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${(p) =>
    p.theme.name === 'light'
      ? '#ffffff'
      : 'linear-gradient(135deg, #001a4d 0%, #002b80 100%)'};
  padding: 8px 16px;
  border-radius: 16px;
  border: 1px solid ${(p) =>
    p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)'};
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  margin: 10px auto 20px auto;
  align-self: center;
  width: fit-content;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    justify-content: center;
  }
`

export const SubmenuNavTab = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: none;
  font-family: 'Montserrat', sans-serif;

  ${(p) =>
    p.$active
      ? css`
          background: ${p.theme.name === 'light'
            ? 'linear-gradient(135deg, #00205b 0%, #003399 100%)'
            : '#a6c10b'};
          color: ${p.theme.name === 'light' ? '#ffffff' : '#001a4d'};
          box-shadow: 0 4px 12px ${p.theme.name === 'light' ? 'rgba(0, 32, 91, 0.3)' : 'rgba(166, 193, 11, 0.4)'};
        `
      : css`
          background: transparent;
          color: ${p.theme.name === 'light' ? '#64748b' : '#94a3b8'};

          &:hover {
            background: ${p.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)'};
            color: ${p.theme.name === 'light' ? '#0f172a' : '#ffffff'};
          }
        `}
`

export const DailyMatrixContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 1850px;
  margin: 0 auto;
  padding: 12px 32px 80px 32px;
  box-sizing: border-box;
  animation: ${fadeInUp} 0.35s ease;

  @media (max-width: 1200px) {
    padding: 12px 20px 70px 20px;
  }

  @media (max-width: 768px) {
    padding: 10px 12px 60px 12px;
  }
`

export const DailyMatrixTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  background: ${(p) => (p.theme.name === 'light' ? '#ffffff' : '#001a4d')};
  border-radius: 18px;
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)')};
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);

  /* Ocultar barra de rolagem nativa para manter estritamente a barra dinâmica */
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
  &::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
`

export const DailyTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.78rem;
  font-family: 'Montserrat', sans-serif;
  text-align: right;
  white-space: nowrap;

  th, td {
    padding: 7px 9px;
    border-bottom: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
    border-right: 1px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  }

  /* Coluna Fixa / Sticky da Família */
  .sticky-col {
    position: sticky;
    left: 0;
    z-index: 3;
    text-align: left;
    font-weight: 700;
    min-width: 180px;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : '#001e54')};
    border-right: 2px solid ${(p) => (p.theme.name === 'light' ? '#94a3b8' : '#a6c10b')};
  }

  thead th {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.4px;
    background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : '#092115')};
    color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  }

  /* Cabeçalho de Grupo de Semanas */
  .week-group-header {
    text-align: center;
    font-size: 0.76rem;
    padding: 8px 12px;
    border-bottom: 2px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.2)')};
  }

  .month-summary {
    background: ${(p) => (p.theme.name === 'light' ? '#dcfce7' : '#092e15')};
    color: ${(p) => (p.theme.name === 'light' ? '#14532d' : '#a6c10b')};
    font-weight: 800;
  }
  .weeks-summary {
    background: ${(p) => (p.theme.name === 'light' ? '#e2f7e9' : '#0a3618')};
    color: ${(p) => (p.theme.name === 'light' ? '#14532d' : '#86efac')};
    font-weight: 800;
  }
  /* Semanas Ímpares (S1, S3, S5) - Tom Verde Floresta Escuro */
  .week-1, .week-3, .week-5 {
    background: ${(p) => (p.theme.name === 'light' ? '#f0fdf4' : '#0f381e')};
    color: ${(p) => (p.theme.name === 'light' ? '#166534' : '#dcfce7')};
    font-weight: 700;
  }
  /* Semanas Pares (S2, S4) - Tom Verde Alternado Mais Claro */
  .week-2, .week-4 {
    background: ${(p) => (p.theme.name === 'light' ? '#e1faea' : '#164e2b')};
    color: ${(p) => (p.theme.name === 'light' ? '#14532d' : '#ffffff')};
    font-weight: 700;
  }

  /* Linha 1: Valor em R$ */
  .row-valor-rs {
    background: ${(p) => (p.theme.name === 'light' ? '#f0f9ff' : 'rgba(2, 132, 199, 0.25)')};
    font-weight: 800;
    color: ${(p) => (p.theme.name === 'light' ? '#0369a1' : '#38bdf8')};

    .sticky-col {
      background: ${(p) => (p.theme.name === 'light' ? '#e0f2fe' : '#034575')};
      color: ${(p) => (p.theme.name === 'light' ? '#0284c7' : '#38bdf8')};
    }
  }

  /* Linha Final: Total Geral em Caixas */
  .row-total-geral {
    background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : 'rgba(166, 193, 11, 0.2)')};
    font-weight: 800;
    border-top: 2px solid ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#a6c10b')};

    .sticky-col {
      background: ${(p) => (p.theme.name === 'light' ? '#f1f5f9' : '#143803')};
      color: ${(p) => (p.theme.name === 'light' ? '#0f172a' : '#a6c10b')};
    }
  }

  tbody tr:hover {
    background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.04)')};
  }
`

export const MetricCellTag = styled.span<{ $tipo?: 'meta' | 'real' | 'pct' | 'tendencia'; $status?: 'green' | 'yellow' | 'red' | 'neutral' }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.72rem;

  ${(p) => {
    if (p.$status === 'green') {
      return css`
        background: ${p.theme.name === 'light' ? '#dcfce7' : 'rgba(34, 197, 94, 0.2)'};
        color: ${p.theme.name === 'light' ? '#15803d' : '#4ade80'};
      `
    }
    if (p.$status === 'yellow') {
      return css`
        background: ${p.theme.name === 'light' ? '#fef9c3' : 'rgba(234, 179, 8, 0.2)'};
        color: ${p.theme.name === 'light' ? '#a16207' : '#facc15'};
      `
    }
    if (p.$status === 'red') {
      return css`
        background: ${p.theme.name === 'light' ? '#fee2e2' : 'rgba(239, 68, 68, 0.2)'};
        color: ${p.theme.name === 'light' ? '#b91c1c' : '#f87171'};
      `
    }
    return css`
      color: inherit;
    `
  }}
`

export const DistPillButton = styled.button<{ $active?: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.76rem;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};

  ${(p) =>
    p.$active
      ? css`
          background: ${p.theme.name === 'light' ? '#00205b' : '#a6c10b'};
          color: ${p.theme.name === 'light' ? '#ffffff' : '#001a4d'};
          border-color: ${p.theme.name === 'light' ? '#00205b' : '#a6c10b'};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        `
      : css`
          background: ${p.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.2)'};
          color: ${p.theme.name === 'light' ? '#475569' : '#94a3b8'};

          &:hover {
            background: ${p.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)'};
            color: ${p.theme.name === 'light' ? '#0f172a' : '#ffffff'};
          }
        `}
`

export const StickyHorizontalScrollWrapper = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  z-index: 50;
  background: ${(p) =>
    p.theme.name === 'light'
      ? 'rgba(255, 255, 255, 0.96)'
      : 'linear-gradient(180deg, rgba(0, 24, 66, 0.92) 0%, rgba(0, 16, 44, 0.98) 100%)'};
  border-top: 1px solid ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(166, 193, 11, 0.3)')};
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
  border-radius: 0 0 16px 16px;
  padding: 3px 0;
  margin-top: -6px;

  /* Suporte Firefox / Padrão W3C - Verde GID */
  scrollbar-width: thin !important;
  scrollbar-color: #a6c10b ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(0, 17, 51, 0.85)')} !important;

  /* Suporte WebKit / Chromium - Verde GID Vibrante */
  &::-webkit-scrollbar {
    height: 12px !important;
    display: block !important;
  }
  &::-webkit-scrollbar-track {
    background: ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(0, 17, 51, 0.85)')} !important;
    border-radius: 10px !important;
  }
  &::-webkit-scrollbar-thumb {
    background: #a6c10b !important;
    border-radius: 10px !important;
    border: 2px solid ${(p) => (p.theme.name === 'light' ? '#e2e8f0' : '#001842')} !important;
    cursor: grab;

    &:hover {
      background: #c3e214 !important;
    }
    &:active {
      cursor: grabbing;
      background: #d4f41b !important;
    }
  }
`

export const FilterDropdownButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(p) => (p.$active ? (p.theme.name === 'light' ? '#15803d' : '#a6c10b') : (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.16)'))};
  background: ${(p) => (p.$active ? (p.theme.name === 'light' ? '#f0fdf4' : 'rgba(166, 193, 11, 0.15)') : (p.theme.name === 'light' ? '#ffffff' : 'rgba(0, 24, 66, 0.6)'))};
  color: ${(p) => (p.$active ? (p.theme.name === 'light' ? '#15803d' : '#a6c10b') : (p.theme.name === 'light' ? '#334155' : '#e2e8f0'))};

  &:hover {
    border-color: ${(p) => (p.theme.name === 'light' ? '#94a3b8' : '#a6c10b')};
    background: ${(p) => (p.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.08)')};
  }

  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1px 7px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 700;
    background: ${(p) => (p.$active ? (p.theme.name === 'light' ? '#15803d' : '#a6c10b') : (p.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.15)'))};
    color: ${(p) => (p.$active ? (p.theme.name === 'light' ? '#ffffff' : '#001842') : 'inherit')};
  }
`

export const WeekPillButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 0.74rem;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(p) => (p.$active ? '#a6c10b' : (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.16)'))};
  background: ${(p) => (p.$active ? (p.theme.name === 'light' ? '#00205b' : '#a6c10b') : (p.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.2)'))};
  color: ${(p) => (p.$active ? (p.theme.name === 'light' ? '#ffffff' : '#001842') : (p.theme.name === 'light' ? '#475569' : '#94a3b8'))};

  &:hover {
    border-color: #a6c10b;
  }
`

export const ClearFiltersButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.74rem;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px dashed ${(p) => (p.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.2)')};
  background: transparent;
  color: ${(p) => (p.theme.name === 'light' ? '#64748b' : '#94a3b8')};

  &:hover {
    border-color: #ef4444;
    color: #ef4444;
    background: rgba(239, 68, 68, 0.08);
  }
`
